/**
 * 图表配置生成器
 * 职责: 生成ECharts配置对象
 * 限制: <150行
 */

import type { TrendData } from '../../../types/exchange-rate-types';

export const CURRENCY_COLORS: { [key: string]: string } = {
    USD: '#2F54EB', EUR: '#13C2C2', JPY: '#FA8C16', GBP: '#722ED1',
    AUD: '#52C41A', CAD: '#EB2F96', CHF: '#F5222D', HKD: '#1890FF',
    SGD: '#A0D911', KRW: '#FA541C'
};

export const getChartOption = (
    trendData: TrendData[],
    selectedCurrencies: string[]
) => {
    if (!trendData.length) return {};

    const dates = trendData.map(item => item.date);
    const series = buildSeries(trendData, selectedCurrencies);

    return {
        grid: buildGrid(),
        tooltip: buildTooltip(),
        legend: buildLegend(selectedCurrencies),
        xAxis: buildXAxis(dates),
        yAxis: buildYAxis(),
        series: series
    };
};

const buildSeries = (trendData: TrendData[], currencies: string[]) => {
    return currencies.map(currency => {
        const color = CURRENCY_COLORS[currency] || '#1890ff';
        return {
            name: currency,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            showSymbol: true,
            label: buildSeriesLabel(color),
            lineStyle: buildLineStyle(color),
            itemStyle: buildItemStyle(color),
            areaStyle: buildAreaStyle(color),
            emphasis: { focus: 'series', scale: true },
            data: trendData.map(item => item.rates[currency] || null)
        };
    });
};

const buildSeriesLabel = (color: string) => ({
    show: true,
    position: 'top',
    formatter: (params: any) => Number(params.value).toFixed(4),
    fontSize: 10,
    color: color
});

const buildLineStyle = (color: string) => ({
    width: 3,
    color: color,
    shadowColor: 'rgba(0,0,0,0.1)',
    shadowBlur: 10,
    shadowOffsetY: 5
});

const buildItemStyle = (color: string) => ({
    color: color,
    borderWidth: 2,
    borderColor: '#fff'
});

const buildAreaStyle = (color: string) => ({
    color: {
        type: 'linear',
        x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
            { offset: 0, color: color },
            { offset: 1, color: 'rgba(255, 255, 255, 0)' }
        ],
        global: false
    },
    opacity: 0.2
});

const buildGrid = () => ({
    left: '2%', right: '2%', bottom: '5%',
    top: '15%', containLabel: true
});

const buildTooltip = () => ({
    trigger: 'axis',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    padding: 12,
    textStyle: { color: '#333' },
    axisPointer: {
        type: 'line',
        lineStyle: { color: '#ddd', width: 1, type: 'dashed' }
    },
    formatter: formatTooltip
});

const formatTooltip = (params: any) => {
    let result = `<div style="margin-bottom: 8px; font-weight: 600; color: #666;">${params[0].axisValue}</div>`;
    params.forEach((param: any) => {
        if (param.value !== null && param.value !== undefined) {
            result += `
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; min-width: 160px;">
                    <span style="display: flex; align-items: center;">
                        <span style="display:inline-block;margin-right:6px;border-radius:50%;width:8px;height:8px;background-color:${param.color};"></span>
                        <span style="color: #666;">${param.seriesName}</span>
                    </span>
                    <span style="font-weight: 600; font-family: monospace;">¥${Number(param.value).toFixed(4)}</span>
                </div>`;
        }
    });
    return result;
};

const buildLegend = (currencies: string[]) => ({
    data: currencies,
    icon: 'circle',
    itemGap: 20,
    textStyle: { color: '#666' },
    top: 0
});

const buildXAxis = (dates: string[]) => ({
    type: 'category',
    boundaryGap: false,
    data: dates,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: '#999', margin: 15 }
});

const buildYAxis = () => ({
    type: 'value',
    name: '汇率 (CNY)',
    scale: true,
    nameTextStyle: { color: '#ccc', align: 'right', padding: [0, 10, 0, 0] },
    splitLine: { lineStyle: { type: 'dashed', color: '#eee' } },
    axisLabel: { color: '#999' }
});
